import type { AxiosResponse } from "axios";

import { getData, postData } from "@/apis/apiInstance";

import type {
  AddJobsPayload,
  GetJobsPayload,
  AddJobsTypePayload,
  GetJobsListTypePayload,
  RecurringJobsArrayType,
  SingleJobPayload,
  SingleJobTypePayload,
  SingleJobTypeStatusChangePayload,
  SingleRecurringJobsPayload,
  uniqueCheckJobType,
  GetRecurringJobsSettingPayload,
  AddRecurringJobsSettingPayload,
  GetJobCheckListPayloadType,
  GetAllOpenJobCheckListDetailsPayloadType,
  GetAllOpenJobCheckListPayloadType,
  AddJobCheckListPayloadType,
  UpdateJobCheckListPayloadType,
  UpdateAllOpenJobChecklistPayloadType,
  AddJobClosureType,
  LockJobCheckListPayloadType,
} from "@/commonTypes/jobsType";
import type {
  AddDocumentLinkPayloadType,
  AddJobProgressCommentPayload,
  DownloadJobProgressPayloadType,
  DownloadRecurringJobReportPayloadType,
  GetAllAssignedClientsOpenJobsPayloadType,
  GetAllDocumentsLinkPayloadType,
  GetJobProgressCommentsPayload,
  GetRecurringJobReportPayloadType,
  ShareJobsWithTeamMembersPayloadType,
} from "@/commonTypes/listing";

import { formatDate, getCSRFToken } from "@/utils/commonFunctions";

export async function getJobsTypeListApi(
  data: GetJobsListTypePayload
): Promise<AxiosResponse> {
  const { token, limit, offset, search_term, recurring, status } = data;

  return await getData(`finac-crm/job-type/list`, {
    params: {
      offset,
      limit,
      search_term,
      recurring,
      status,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getSingleJobsTypeApi(
  data: SingleJobTypePayload
): Promise<AxiosResponse> {
  const { token, id, with_job_count } = data;

  return await getData(`finac-crm/job-type`, {
    params: {
      id,
      with_job_count,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function addRecurringJobsDetailsApi(
  data: AddRecurringJobsSettingPayload
): Promise<AxiosResponse> {
  const { token, clientCode, jobDetails } = data;
  const newJobDetails = jobDetails?.map((data: RecurringJobsArrayType) => {
    return {
      date_changed: data?.dateChanged,
      job_type: data?.jobType?.value,
      description: data?.description,
      next_job_date: data?.nextJobDate ? data?.nextJobDate : null,
      engagement_cutoff_date: data?.engagementCutoffDate
        ? formatDate(data?.engagementCutoffDate)
        : null,
      manager: data?.manager?.value,
      responsible: data?.responsible?.value,
      govt_officer: data?.govtOfficer?.value,
    };
  });

  const finalData = {
    client_code: clientCode,
    job_details: newJobDetails,
  };

  return await postData(`finac-crm/recurring-job/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getSingleRecurringJobsDetailsApi(
  data: GetRecurringJobsSettingPayload
): Promise<AxiosResponse> {
  const {
    token,
    clientCode,
    limit,
    offset,
    searchTerm,
    sortByClientCode,
    sortByJobType,
  } = data;
  return await getData(`finac-crm/recurring-job/list`, {
    params: {
      client_code: clientCode,
      limit,
      offset,
      search_term: searchTerm,
      client_code_sorted_order: sortByClientCode,
      job_type_sorted_order: sortByJobType,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

// addJobsTypeListApi

export async function addJobsTypeListApi(
  jobData: AddJobsTypePayload
): Promise<AxiosResponse> {
  const { token, data } = jobData;
  const finalData = {
    ...data,
  };

  return await postData(`finac-crm/job-type/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
export async function changeJobsTypeStatusApi(
  jobData: SingleJobTypeStatusChangePayload
): Promise<AxiosResponse> {
  const { token, id, status } = jobData;
  const finalData = {
    id,
    status,
  };
  return await postData(`finac-crm/job-type/change/status`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateJobsTypeListApi(
  jobData: AddJobsTypePayload
): Promise<AxiosResponse> {
  const { token, data } = jobData;
  const finalData = {
    ...data,
  };
  return await postData(`finac-crm/job-type/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getJobsListApi(
  data: GetJobsPayload
): Promise<AxiosResponse> {
  const {
    token,
    limit,
    offset,
    search_term,
    endDate,
    jobType,
    status,
    filterStatus,
    filterStartDate,
    filterEndDate,
    managerId,
    clientCode,
    clientName,
    sortByClientCode,
    responsibleId,
    sharedJob,
    jobPriority,
    internalStatus,
  } = data;
  const finalData = {
    limit,
    offset,
    search_term,
    job_type_sorted_order: jobType,
    client_code_sorted_order: sortByClientCode,
    company_name_sorted_order: clientName,
    target_end_date_sorted_order: endDate,
    open_jobs_asc_order: status ? +status : "",
    ...(managerId && { job_manager: managerId }),
    ...(responsibleId && { currently_responsible: responsibleId }),
    target_start_date: filterStartDate && formatDate(filterStartDate),
    target_end_date: filterEndDate && formatDate(filterEndDate),
    status: filterStatus,
    client_code: clientCode,
    shared_job: sharedJob,
    job_priority: jobPriority,
    internal_status: internalStatus,
  };
  return await getData(`finac-crm/job/list`, {
    params: {
      ...finalData,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getFutureJobsListApi(
  data: GetJobsPayload
): Promise<AxiosResponse> {
  const { token, limit, offset, search_term, clientCode } = data;
  const finalData = {
    limit,
    offset,
    search_term,
    client_code: clientCode,
  };
  return await getData(`finac-crm/future-job/list`, {
    params: {
      ...finalData,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function addJobsApi(
  jobData: AddJobsPayload
): Promise<AxiosResponse> {
  const { token, data } = jobData;
  const finalData = {
    ...data,
  };
  return await postData(`finac-crm/job/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getSingleJobsApi(
  data: SingleJobPayload
): Promise<AxiosResponse> {
  const { token, id } = data;

  return await getData(`finac-crm/job`, {
    params: {
      job_id: id,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

// updateJobsApi
export async function updateJobsApi(
  jobData: AddJobsPayload
): Promise<AxiosResponse> {
  const { token, data } = jobData;
  const finalData = {
    ...data,
  };
  return await postData(`finac-crm/job/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getRecurringResponsiblePeopleApi(
  data: SingleRecurringJobsPayload
): Promise<AxiosResponse> {
  const { token, client_code, job_type } = data;

  return await getData(`finac-crm/recurring-job`, {
    params: {
      client_code,
      job_type,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

//Job Comments

export async function getJobCommentsDetailsApi(
  data: GetJobProgressCommentsPayload
): Promise<AxiosResponse> {
  const { token, job_id } = data;

  return await getData(`finac-crm/jobs/comment/list`, {
    params: {
      job_id,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function addJobCommentsApi(
  jobData: AddJobProgressCommentPayload
): Promise<AxiosResponse> {
  const { token, job_id, comment } = jobData;
  const finalData = {
    comment,
    job_id,
  };
  return await postData(`finac-crm/jobs/comment/create`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

//Job Links
export async function getJobLinksDetailsApi(
  data: GetAllDocumentsLinkPayloadType
): Promise<AxiosResponse> {
  const { job_id, token } = data;

  return await getData(`finac-crm/jobs/link/list`, {
    params: {
      job_id,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function addJobLinkApi(
  jobData: AddDocumentLinkPayloadType
): Promise<AxiosResponse> {
  const { link, job_id, description, token, sharepoint_link } = jobData;

  let finalData = {
    link,
    job_id,
    description,
    sharepoint_link,
  };
  return await postData(`finac-crm/jobs/link/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getUserMapedClientJobListApi(
  data: any
): Promise<AxiosResponse> {
  const {
    token,
    search_term,
    status,
    jobType,
    sortByClientCode,
    clientName,
    endDate,
    clientCode,
    jobPriority,
    internalStatus,
    limit,
    offset,
    tomorrowEndDate,
  } = data;

  return await getData(`finac-crm/client/job/list`, {
    params: {
      search_term: search_term ?? "",
      limit,
      offset,
      status,
      job_priority: jobPriority,
      tomorrow_end_date: tomorrowEndDate,
      internal_status: internalStatus,
      ...(jobType && { job_type_sorted_order: jobType }),
      ...(sortByClientCode && { client_code_sorted_order: sortByClientCode }),
      ...(clientName && { company_name_sorted_order: clientName }),
      ...(endDate && { target_end_date_sorted_order: endDate }),
      ...(clientCode && { client_code: clientCode }),
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getJobCheckListApi(
  data: GetJobCheckListPayloadType
): Promise<AxiosResponse> {
  const { token, jobTypeId, limit, offset, searchTerm } = data;

  return await getData(`finac-crm/job-type/checklist/list`, {
    params: {
      job_type: jobTypeId,
      limit,
      offset,
      search_term: searchTerm,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getAllOpenJobChecklistSingleDataApi(
  data: GetAllOpenJobCheckListDetailsPayloadType
): Promise<AxiosResponse> {
  const { token, jobChecklistId, jobId } = data;

  return await getData(`finac-crm/job/checklist/detail`, {
    params: {
      job_checklist_id: jobChecklistId,
      job_id: jobId,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getAllOpenJobChecklistApi(
  data: GetAllOpenJobCheckListPayloadType
): Promise<AxiosResponse> {
  const { token, jobId, limit, offset, searchTerm } = data;

  return await getData(`finac-crm/job/checklist/list`, {
    params: {
      job_id: jobId,
      limit,
      offset,
      search_term: searchTerm,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getJobCheckListSingleApi(
  data: GetJobCheckListPayloadType
): Promise<AxiosResponse> {
  const { token, jobTypeId, limit, offset } = data;

  return await getData(`finac-crm/job-type/checklist/detail`, {
    params: {
      jobtype_checklist_id: jobTypeId,
      limit,
      offset,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function addJobsChecklistApi(
  checkListData: AddJobCheckListPayloadType
): Promise<AxiosResponse> {
  const { token, data } = checkListData;
  const finalData = {
    ...data,
  };
  return await postData(`finac-crm/job-type/checklist/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateJobsChecklistApi(
  jobData: UpdateJobCheckListPayloadType
): Promise<AxiosResponse> {
  const { token, data } = jobData;
  const finalData = {
    ...data,
  };
  return await postData(`finac-crm/job-type/checklist/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function lockJobsChecklistApi(
  jobData: LockJobCheckListPayloadType
): Promise<AxiosResponse> {
  const { token, jobId, checkListId, shouldLock } = jobData;
  return await postData(
    `finac-crm/job-wise/checklist/lock`,
    {
      job_id: jobId,
      check_list_id: checkListId,
      should_lock: shouldLock,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getUserMapedClientShareJobListApi(
  data: any
): Promise<AxiosResponse> {
  const {
    token,
    search_term,
    status,
    jobType,
    sortByClientCode,
    clientName,
    endDate,
    clientCode,
    jobPriority,
  } = data;

  return await getData(`finac-crm/job/shared/list`, {
    params: {
      search_term: search_term ?? "",
      limit: 10000,
      offset: 0,
      status,
      job_priority: jobPriority,
      ...(jobType && { job_type_sorted_order: jobType }),
      ...(sortByClientCode && { client_code_sorted_order: sortByClientCode }),
      ...(clientName && { company_name_sorted_order: clientName }),
      ...(endDate && { target_end_date_sorted_order: endDate }),
      ...(clientCode && { client_code: clientCode }),
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

//  unique check job type
export async function checkUniqueJobTypeApi(
  data: uniqueCheckJobType
): Promise<AxiosResponse> {
  const { token, operation, job_type, id } = data;
  const finalParams = {
    operation,
    job_type,
    id,
  };
  return await getData(`finac-crm/job-type/unique/check`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadUserOpenJobsReportApi(
  data: any
): Promise<AxiosResponse> {
  const { token, user_id, search_term } = data;
  let finalParams = {
    user_id: user_id ? user_id : null,
    search_term,
  };

  return await getData(`finac-crm/client/job/download`, {
    params: {
      ...finalParams,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function shareJobsWithTeamMembersApi(
  data: ShareJobsWithTeamMembersPayloadType
): Promise<AxiosResponse> {
  const { token, id, jobId, accessMemberIds } = data;

  let finalParams = {
    _id: id,
    job_id: jobId,
    access_member_ids: accessMemberIds,
  };
  return await postData(`finac-crm/job/share`, finalParams, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function addJobClosureApi(
  jobData: AddJobClosureType
): Promise<AxiosResponse> {
  const { job_id, token, client_code, notify_email } = jobData;

  let finalData = {
    job_id,
    client_code,
    notify_email,
  };
  return await postData(`finac-crm/job/closure/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateAllOpenJobChecklistApi(
  jobData: UpdateAllOpenJobChecklistPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    jobId,
    checkListId,
    procedureId,
    comment,
    status,
    isBeingApproved,
    attachedLink,
  } = jobData;
  const finalData = {
    job_id: jobId,
    check_list_id: checkListId,
    procedure_id: procedureId,
    comment: comment,
    status: status,
    is_being_approved: isBeingApproved,
    attachments: attachedLink,
  };
  return await postData(`finac-crm/job/checklist/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getCommentDueTomorrowJobListApi(
  data: GetAllAssignedClientsOpenJobsPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    search_term,
    status,
    jobType,
    sortByClientCode,
    clientName,
    endDate,
    clientCode,
    jobPriority,
    internalStatus,
    limit,
    offset,
  } = data;

  return await getData(`finac-crm/comment-tomorrow-due/job/list`, {
    params: {
      search_term: search_term ?? "",
      limit,
      offset,
      status,
      job_priority: jobPriority,
      internal_status: internalStatus,
      ...(jobType && { job_type_sorted_order: jobType }),
      ...(sortByClientCode && { client_code_sorted_order: sortByClientCode }),
      ...(clientName && { company_name_sorted_order: clientName }),
      ...(endDate && { target_end_date_sorted_order: endDate }),
      ...(clientCode && { client_code: clientCode }),
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
export async function downloadJobProgressReportsApi(
  data: DownloadJobProgressPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    search_term,
    endDate,
    jobType,
    status,
    filterStatus,
    filterStartDate,
    filterEndDate,
    managerId,
    clientCode,
    clientName,
    sortByClientCode,
    responsibleId,
    sharedJob,
    jobPriority,
  } = data;

  return await getData(`finac-crm/job/list/download`, {
    params: {
      search_term,
      job_type_sorted_order: jobType,
      client_code_sorted_order: sortByClientCode,
      company_name_sorted_order: clientName,
      target_end_date_sorted_order: endDate,
      open_jobs_asc_order: status ? +status : "",
      ...(managerId && { job_manager: managerId }),
      ...(responsibleId && { currently_responsible: responsibleId }),
      target_start_date: filterStartDate && formatDate(filterStartDate),
      target_end_date: filterEndDate && formatDate(filterEndDate),
      status: filterStatus,
      client_code: clientCode,
      shared_job: sharedJob,
      job_priority: jobPriority,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getRecurringJobReportApi(
  data: GetRecurringJobReportPayloadType
): Promise<AxiosResponse> {
  const { token, manager, currently_responsible } = data;
  return await getData(`/finac-crm/recurring-job/report`, {
    params: {
      manager,
      currently_responsible,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadRecurringJobReportApi(
  data: DownloadRecurringJobReportPayloadType
): Promise<AxiosResponse> {
  const { token, manager, currently_responsible } = data;

  return await getData(`finac-crm/recurring-job/report/download`, {
    params: {
      manager,
      currently_responsible,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
